// Overview tab: at-a-glance snapshot, recent activity, internal notes.
// First fully-implemented tab in Phase B.

import { useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';

function formatCurrency(value) {
  if (value == null || isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}
function formatDate(d) {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}
function formatRelative(d) {
  if (!d) return '';
  const now = Date.now();
  const then = new Date(d).getTime();
  const diff = (now - then) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(d);
}

export default function OverviewTab({ household, members, totals, recentActivity, reload }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {/* Left + middle column: snapshot widgets */}
      <div className="col-span-2 space-y-4">
        <SnapshotCard household={household} totals={totals} />
        <MembersPreview members={members} />
      </div>

      {/* Right column: notes + recent activity */}
      <div className="space-y-4">
        <InternalNotesCard household={household} reload={reload} />
        <RecentActivityCard activity={recentActivity} members={members} />
      </div>
    </div>
  );
}

// ─── Snapshot card ────────────────────────────────────────────────
function SnapshotCard({ household, totals }) {
  const rows = [
    { label: 'Members',          value: `${totals.active_count} active${totals.member_count > totals.active_count ? `, ${totals.member_count - totals.active_count} inactive` : ''}` },
    { label: 'Services',         value: totals.divisions.length ? totals.divisions.map(d => d.toUpperCase()).join(' + ') : 'None' },
    { label: 'Annual revenue',   value: formatCurrency(totals.annual_revenue) },
    { label: 'Monthly recurring', value: formatCurrency(totals.monthly_recurring) },
    { label: 'AUM',              value: formatCurrency(totals.aum) },
    { label: 'Tax plan fees',    value: formatCurrency(totals.tax_plan_fee) },
    { label: 'State',            value: household.state || '—' },
    { label: 'Primary email',    value: household.primary_email || '—' },
    { label: 'Phone',            value: household.phone || '—' },
    { label: 'Referral source',  value: household.referral_source || '—' },
  ];
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold mb-4">Snapshot</h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {rows.map(r => (
          <div key={r.label} className="flex items-baseline justify-between gap-3 border-b border-gray-100 pb-2">
            <span className="text-[11px] uppercase tracking-wider text-[#888]">{r.label}</span>
            <span className="text-[13px] text-navy font-bold text-right truncate">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Members preview ──────────────────────────────────────────────
function MembersPreview({ members }) {
  if (members.length === 0) {
    return (
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold mb-3">Members</h3>
        <p className="text-[12px] text-[#888]">No members in this household yet.</p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold">Members</h3>
        <span className="text-[11px] text-[#888]">{members.length} total</span>
      </div>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 text-[10px] uppercase tracking-wider text-[#888]">Name</th>
            <th className="text-left py-2 text-[10px] uppercase tracking-wider text-[#888]">Division</th>
            <th className="text-left py-2 text-[10px] uppercase tracking-wider text-[#888]">Tier</th>
            <th className="text-left py-2 text-[10px] uppercase tracking-wider text-[#888]">Status</th>
            <th className="text-right py-2 text-[10px] uppercase tracking-wider text-[#888]">Annual Revenue</th>
          </tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr key={m.id} className="border-b border-gray-100">
              <td className="py-2 text-navy font-bold">{m.name}</td>
              <td className="py-2 text-[#666]">{(m.division || '').toUpperCase()}</td>
              <td className="py-2 text-[#666]">{(m.service_tier || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
              <td className="py-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[2px] ${
                  m.status === 'Active' ? 'bg-deep-teal/10 text-deep-teal' : 'bg-gray-100 text-[#888]'
                }`}>{m.status || 'Unknown'}</span>
              </td>
              <td className="py-2 text-right text-navy">{formatCurrency(Number(m.annual_revenue) || (Number(m.monthly_revenue) || 0) * 12)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Internal notes (inline-editable) ─────────────────────────────
function InternalNotesCard({ household, reload }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(household.internal_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/data/households/${encodeURIComponent(household.id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ internal_notes: draft }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      setEditing(false);
      reload();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold">Internal Notes</h3>
        {!editing && (
          <button
            onClick={() => { setDraft(household.internal_notes || ''); setEditing(true); }}
            className="text-[11px] text-bullogic-blue hover:text-navy transition-colors"
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
            className="w-full text-[12px] text-navy border border-gray-200 rounded-[4px] p-2 focus:outline-none focus:border-bullogic-blue resize-y"
            placeholder="Staff-only notes about this household…"
          />
          {error && <p className="text-[11px] text-rose mt-1">{error}</p>}
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={save}
              disabled={saving}
              className="text-[12px] font-bold text-white px-3 py-1.5 bg-navy rounded-[4px] hover:bg-navy/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => { setEditing(false); setError(null); }}
              className="text-[12px] text-[#888] px-3 py-1.5 hover:text-navy transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : household.internal_notes ? (
        <p className="text-[12px] text-navy whitespace-pre-wrap leading-relaxed">{household.internal_notes}</p>
      ) : (
        <p className="text-[12px] text-[#888] italic">No notes yet. Click Edit to add.</p>
      )}
    </div>
  );
}

// ─── Recent activity ──────────────────────────────────────────────
function RecentActivityCard({ activity, members }) {
  const memberLookup = Object.fromEntries(members.map(m => [m.id, m.name]));
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <h3 className="text-[11px] uppercase tracking-wider text-deep-teal font-bold mb-3">Recent Activity</h3>
      {activity.length === 0 ? (
        <p className="text-[12px] text-[#888] italic">No recent activity for this household.</p>
      ) : (
        <ul className="space-y-2.5">
          {activity.slice(0, 10).map(a => (
            <li key={a.id} className="text-[11px] border-b border-gray-100 pb-2 last:border-0">
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-bold text-navy">{a.action || 'update'}</span>
                <span className="text-[10px] text-[#888]" title={a.changed_at}>{formatRelative(a.changed_at)}</span>
              </div>
              <div className="text-[#666] mt-0.5">
                {a.table_name}
                {a.record_id && memberLookup[a.record_id] && ` · ${memberLookup[a.record_id]}`}
              </div>
              {a.summary && <div className="text-[#888] mt-0.5 italic">{a.summary}</div>}
              {a.changed_by && <div className="text-[10px] text-[#aaa] mt-0.5">by {a.changed_by}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
